import { ServerTransport, FrontendTransport } from 'sb-lib/types';
import {
  SbSocketAbstract,
  SbSocketConstructArgsBase,
  SbSocketOnEvent,
  SbSocketEmitEvent,
  SbSocketConnectArgsBase,
  SbSocketOnEventName,
  SbSocketEmitAction,
} from 'sb-lib/sb-socket-abstract';
import { TOKEN_AUTHENTICATION_KEY } from 'shared/utils/authentication-token';
import { getBrowserSessionSocketMetaSid } from 'shared/utils/helpers';

type SbSocketConstructArgsFrontend = SbSocketConstructArgsBase;

interface SbSocketConnectArgsFrontend extends SbSocketConnectArgsBase {
  passive?: boolean;
  kickOther?: boolean;
  authenticationToken: string | null;
  orgId: number; // orgId can change per user
}

enum _SbSocketEmitActionFrontend {
  introduction = 'introduction',
  associateVisitor = 'associate_visitor',
  dissociateVisitor = 'dissociate_visitor',
  updateVisitorInteraction = 'update_visitor_interaction',
  kickAgent = 'kick_agent',
  chatMessage = 'chat_message',
  chatTyping = 'chat_typing',
  acceptedCall = 'accepted_call',
  readChatMessages = 'read_chat_messages',
  rrwebEventAcks = 'rrweb_event_acks',
  visitorRelay = 'visitor_relay',
  webRtcConnection = 'webrtc_connection',
  resetSpyOptin = 'reset_spy_optin',
  salesfloorChatMessage = 'salesfloor_chat_message',
}
export type SbSocketEmitActionFrontend = _SbSocketEmitActionFrontend | SbSocketEmitAction;
export const SbSocketEmitActionFrontend = {
  ..._SbSocketEmitActionFrontend,
  ...SbSocketEmitAction,
};
const EA = SbSocketEmitActionFrontend;

/** Public interface to emit */
interface SbSocketEmitEventsFrontend extends SbSocketEmitEvent {
  [EA.introduction]: {
    response: ServerTransport['Frontend']['Introduction'];
  };
  [EA.associateVisitor]: {
    args: FrontendTransport['Server']['AssociateVisitor'];
    response: ServerTransport['Frontend']['AssociateVisitor'];
  };
  [EA.dissociateVisitor]: {
    args: FrontendTransport['Server']['DissociateVisitor'];
    response: ServerTransport['Frontend']['DissociateVisitor'];
  };
  [EA.updateVisitorInteraction]: {
    args: FrontendTransport['Server']['UpdateVisitorInteraction'];
    response: ServerTransport['Frontend']['UpdateVisitorInteraction'];
  };
  [EA.kickAgent]: {
    args: FrontendTransport['Server']['KickAgent'];
  };
  [EA.chatMessage]: {
    args: FrontendTransport['Server']['ChatMessage'];
    response: ServerTransport['Frontend']['ChatMessageResponse'];
  };
  [EA.chatTyping]: {
    args: FrontendTransport['Server']['ChatTyping'];
  };
  [EA.readChatMessages]: {
    args: FrontendTransport['Server']['ReadChatMessages'];
  };
  [EA.rrwebEventAcks]: {
    args: FrontendTransport['Server']['RrwebEventAcks'];
  };
  [EA.subscribe]: { args: FrontendTransport['Server']['Subscribe'] };
  [EA.unsubscribe]: { args: FrontendTransport['Server']['Unsubscribe'] };
  [EA.visitorRelay]: { args: FrontendTransport['Server']['VisitorRelay'] };
  [EA.webRtcConnection]: { response: ServerTransport['Frontend']['WebRtcConnection'] };
  [EA.resetSpyOptin]: { args: FrontendTransport['Server']['ResetSpyOptin'] };
  [EA.salesfloorChatMessage]: {
    args: FrontendTransport['Server']['SalesfloorChatMessage'];
    response: ServerTransport['Frontend']['SalesfloorChatMessageResponse'];
  };
}

// Frontend ws events
enum SbSocketAbsorbActionFrontend {
  disconnecting = 'disconnecting',
  visitorUpdates = 'client_updates',
  visitorsSync = 'clients_sync',
  visitorsCount = 'clients_count',
  updateUsage = 'update_usage',
  destroyActivePeer = 'destroy_active_peer',
  availability = 'availability',
  buildInformation = 'build_information',
  rrwebEvent = 'rrweb_event',
  rrwebSnapshotTicket = 'rrweb_snapshot_ticket',
  pubsubMessage = 'pubsub_message',
  callEndedDetails = 'call_ended_details',
}

// Enum union hack
// https://github.com/microsoft/TypeScript/issues/17592#issuecomment-449440944
export type SbSocketOnEventNameFrontend =
  | SbSocketAbsorbActionFrontend
  | SbSocketOnEventName;
export const SbSocketOnEventNameFrontend = {
  ...SbSocketAbsorbActionFrontend,
  ...SbSocketOnEventName,
};

const OEN = SbSocketOnEventNameFrontend;
export interface SbSocketOnEventFrontend extends SbSocketOnEvent {
  [OEN.visitorUpdates]: ServerTransport['Frontend']['VisitorUpdates'];
  [OEN.visitorsSync]: ServerTransport['Frontend']['VisitorsSync'];
  [OEN.visitorsCount]: ServerTransport['Frontend']['VisitorsCount'];
  [OEN.updateUsage]: ServerTransport['Frontend']['UpdateUsage'];
  [OEN.destroyActivePeer]: ServerTransport['Frontend']['DestroyActivePeer'];
  [OEN.availability]: ServerTransport['Frontend']['Availability'];
  [OEN.buildInformation]: ServerTransport['Frontend']['BuildInformation'];
  [OEN.rrwebEvent]: ServerTransport['Frontend']['RrwebEvent'];
  [OEN.rrwebSnapshotTicket]: ServerTransport['Frontend']['RrwebSnapshotTicket'];
  [OEN.pubsubMessage]: ServerTransport['Frontend']['PubsubMessage'];
  [OEN.callEndedDetails]: ServerTransport['Frontend']['CallEndedDetails'];
}

export class SbSocketFrontend extends SbSocketAbstract<
  SbSocketConstructArgsFrontend,
  SbSocketConnectArgsFrontend,
  SbSocketOnEventFrontend,
  SbSocketEmitEventsFrontend
> {
  protected getMetaSid = getBrowserSessionSocketMetaSid;

  constructor(args: SbSocketConstructArgsFrontend) {
    super(args);
    this.addChattyAction(SbSocketOnEventNameFrontend.updateUsage);
    this.addChattyAction(SbSocketOnEventNameFrontend.actionResponse);
    this.addChattyAction(SbSocketOnEventNameFrontend.rrwebEvent);
    this.addChattyAction(SbSocketOnEventNameFrontend.visitorUpdates);
    this.addChattyAction(SbSocketOnEventNameFrontend.visitorsCount);
    super.wsOnOpenHandler = this.wsOnOpenHandler;
  }

  protected makeConnectQuery() {
    if (!this.connectArgs) {
      throw new Error('No connectArgs');
    }
    const { passive, kickOther, authenticationToken, orgId } = this.connectArgs;
    return {
      role: 'agent',
      metaSid: this.getMetaSid(),
      orgId,
      ...(passive ? { passive } : {}),
      ...(kickOther ? { kickOther } : {}),
      ...(authenticationToken ? { [TOKEN_AUTHENTICATION_KEY]: authenticationToken } : {}),
      ...super.makeConnectQuery(),
    };
  }
}
